import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import {
  Home,
  Phone,
  Wallet,
  LogOut,
  User,
  Globe,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { CONNECT_COUNTRIES } from "@/config/connect-countries";
import { useTranslation } from "react-i18next";
import { useMissedCallsCount } from "@/hooks/useMissedCallsCount";
import { useIsNative } from "@/hooks/useIsNative";

interface NavItem {
  label: string;
  icon: React.ElementType;
  href: string;
  badge?: string | null;
}

export function ConnectPortalNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation("common");
  const { count: missedCallsCount } = useMissedCallsCount();
  const isNative = useIsNative();


  const callHref = missedCallsCount > 0 ? "/connect-portal/dial?tab=recents" : "/connect-portal/dial";

  // Apple Wallet IA - desktop sidebar mirrors the 4-tab mobile spine, with
  // Chats and Rates pulled out as secondary discovery (still one click away)
  // so the primary nav stays focused on the four canonical anchors.
  const primaryNavItems: NavItem[] = [
    { label: t("home"), icon: Home, href: "/connect-portal/dashboard" },
    ...(isNative ? [{ label: t("call"), icon: Phone, href: callHref }] : []),
    { label: t("wallet", { defaultValue: "Wallet" }), icon: Wallet, href: "/connect-portal/topup" },
    { label: t("account"), icon: User, href: "/connect-portal/me" },
  ];

  const secondaryNavItems: NavItem[] = [
    { label: t("rates", { defaultValue: "Rates" }), icon: Globe, href: "/connect-portal/rates" },
  ];

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      toast.success(t("signedOut"));
      navigate("/connect-portal");
    } catch (error) {
      toast.error(t("failedToSignOut"));
    }
  };

  const isActive = (href: string) => {
    const hrefPath = href.split('?')[0];
    if (hrefPath === "/connect-portal/dashboard") {
      return location.pathname === "/connect-portal" || location.pathname === "/connect-portal/dashboard";
    }
    if (hrefPath === "/connect-portal/rates") {
      const segments = location.pathname.split("/");
      const slug = segments[2];
      if (slug && CONNECT_COUNTRIES[slug]) return true;
    }
    if (hrefPath === "/connect-portal/messages") {
      return location.pathname.startsWith("/connect-portal/messages");
    }
    return location.pathname.startsWith(hrefPath);
  };

  return (
    <div className="flex flex-col h-full">
      <ScrollArea className="flex-1 py-4">
        <nav className="space-y-1 px-3">
          {primaryNavItems.map((item) => (
            <NavLink
              key={item.href}
              to={item.href}
              className={cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200",
                isActive(item.href)
                  ? "bg-primary text-primary-foreground"
                  : "text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground",
                item.badge === 'Soon' && !isActive(item.href) && "opacity-60"
              )}
            >
              <item.icon className="h-5 w-5" />
              <span className="flex-1">{item.label}</span>
              {item.badge === 'Soon' && (
                <span className="min-w-[36px] h-[18px] rounded-full bg-primary/20 text-primary text-[10px] font-bold flex items-center justify-center px-1.5">
                  Soon
                </span>
              )}
              {item.href.startsWith("/connect-portal/dial") && missedCallsCount > 0 && (
                <span className="min-w-[20px] h-[20px] rounded-full bg-destructive text-destructive-foreground text-[11px] font-bold flex items-center justify-center px-1.5">
                  {missedCallsCount > 99 ? '99+' : missedCallsCount}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="mt-6 pt-4 border-t border-sidebar-border px-3">
          <p className="px-3 mb-2 text-xs font-medium text-sidebar-foreground/40">{t("more")}</p>
          <nav className="space-y-1">
            {secondaryNavItems.map((item) => (
              <NavLink
                key={item.href}
                to={item.href}
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200",
                  isActive(item.href)
                    ? "bg-primary text-primary-foreground"
                    : "text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground"
                )}
              >
                <item.icon className="h-5 w-5" />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>
        </div>
      </ScrollArea>

      <div className="border-t border-sidebar-border py-4 px-3">
        <Button
          variant="ghost"
          className="w-full justify-start gap-3 px-3 py-2.5 text-sm font-medium text-sidebar-foreground/70 hover:bg-destructive/10 hover:text-destructive"
          onClick={handleSignOut}
        >
          <LogOut className="h-5 w-5" />
          <span>{t("signOut")}</span>
        </Button>
      </div>
    </div>
  );
}
